import { VIDEO_V1_MAX_ORIGINAL_BYTE_LENGTH } from "@aasaan-share/protocol";

export interface DownloadSink {
  readonly bytesWritten: number;
  abort(reason?: unknown): Promise<void>;
  commit(): Promise<Blob | null>;
  write(chunk: Uint8Array): Promise<void>;
}

export interface SaveFilePickerOptions {
  readonly suggestedName?: string;
  readonly types?: readonly {
    readonly accept: Readonly<Record<string, readonly string[]>>;
    readonly description?: string;
  }[];
}

interface FilePickerWindow extends Window {
  showSaveFilePicker?: (
    options?: SaveFilePickerOptions,
  ) => Promise<FileSystemFileHandle>;
}

export class MemoryDownloadSink implements DownloadSink {
  readonly #chunks: ArrayBuffer[] = [];
  #aborted = false;
  #bytesWritten = 0;

  constructor(
    private readonly mimeType: string,
    private readonly maximumBytes = VIDEO_V1_MAX_ORIGINAL_BYTE_LENGTH,
  ) {}

  get bytesWritten(): number {
    return this.#bytesWritten;
  }

  async write(chunk: Uint8Array): Promise<void> {
    if (this.#aborted) throw new Error("Download was cancelled");
    if (this.#bytesWritten + chunk.byteLength > this.maximumBytes) {
      await this.abort();
      throw new Error("Download exceeds the browser memory limit");
    }
    this.#chunks.push(chunk.slice().buffer as ArrayBuffer);
    this.#bytesWritten += chunk.byteLength;
  }

  async commit(): Promise<Blob> {
    if (this.#aborted) throw new Error("Download was cancelled");
    return new Blob(this.#chunks, { type: this.mimeType });
  }

  async abort(): Promise<void> {
    this.#aborted = true;
    this.#chunks.length = 0;
    this.#bytesWritten = 0;
  }
}

export class FileSystemDownloadSink implements DownloadSink {
  #bytesWritten = 0;
  #closed = false;

  constructor(private readonly writable: FileSystemWritableFileStream) {}

  get bytesWritten(): number {
    return this.#bytesWritten;
  }

  async write(chunk: Uint8Array): Promise<void> {
    if (this.#closed) throw new Error("Download sink is closed");
    await this.writable.write(chunk as unknown as BufferSource);
    this.#bytesWritten += chunk.byteLength;
  }

  async commit(): Promise<null> {
    if (!this.#closed) {
      this.#closed = true;
      await this.writable.close();
    }
    return null;
  }

  async abort(reason?: unknown): Promise<void> {
    if (this.#closed) return;
    this.#closed = true;
    await this.writable.abort(reason);
  }
}

export async function createDownloadSink(input: {
  readonly mimeType: string;
  readonly safeFilename: string;
  readonly preferFileSystem?: boolean;
  readonly pickerWindow?: FilePickerWindow;
}): Promise<DownloadSink> {
  const pickerWindow = input.pickerWindow ?? window as FilePickerWindow;
  if (input.preferFileSystem !== false && pickerWindow.showSaveFilePicker) {
    const handle = await pickerWindow.showSaveFilePicker({
      suggestedName: input.safeFilename,
      types: [{
        accept: { [input.mimeType]: [extensionFromName(input.safeFilename)] },
        description: "Original media",
      }],
    });
    return new FileSystemDownloadSink(await handle.createWritable());
  }
  return new MemoryDownloadSink(input.mimeType);
}

export function saveVerifiedBlob(blob: Blob, safeFilename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.download = safeFilename;
  link.href = url;
  link.click();
  queueMicrotask(() => URL.revokeObjectURL(url));
}

export function safeDownloadFilename(
  mediaId: string,
  mimeType: string,
): string {
  const extension: Record<string, string> = {
    "image/avif": "avif",
    "image/heic": "heic",
    "image/heif": "heif",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "video/mp4": "mp4",
    "video/quicktime": "mov",
  };
  const safeId = mediaId.replace(/[^a-z0-9-]/gi, "").slice(0, 36);
  return `aasaan-share-${safeId || "original"}.${extension[mimeType] ?? "bin"}`;
}

function extensionFromName(filename: string): string {
  const match = filename.match(/\.[a-z0-9]+$/i);
  return match?.[0] ?? ".bin";
}
